import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, ArrowRight, ArrowLeft, Loader2, ShieldCheck, Star } from "lucide-react";
import { api } from "@/lib/api";
import { SERVICE_OPTIONS, FORMSPREE_URL } from "@/data/content";
import { toast } from "sonner";

const URGENCY = ["As soon as possible", "Within a week", "Within a month", "Just planning"];
const PROPERTY = ["Residential", "Commercial", "Multi-family"];

const inputCls =
  "w-full bg-black/40 border border-white/15 rounded-sm px-4 py-3 text-white placeholder:text-zinc-500 focus:outline-none focus:border-honey focus:ring-1 focus:ring-honey transition-colors";

export default function QuoteForm({ onSuccess, defaultService = "Residential Roof Replacement" }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [data, setData] = useState({
    service: defaultService,
    urgency: "Within a week",
    property_type: "Residential",
    city: "",
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const next = () => setStep((s) => Math.min(3, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!data.name || !data.phone || !data.email) {
      toast.error("Please add your name, phone, and email.");
      return;
    }
    setLoading(true);
    try {
      // Primary delivery: email the lead to the business via Formspree
      const fsRes = await fetch(FORMSPREE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          service: data.service,
          urgency: data.urgency,
          property_type: data.property_type,
          city: data.city,
          message: data.message,
          _subject: `New Roofing Lead: ${data.name} — ${data.service}`,
        }),
      });
      if (!fsRes.ok) throw new Error("submission_failed");

      // Backup: save to the admin dashboard (best-effort, non-blocking)
      api.post("/leads", { ...data, source: "quote-form" }).catch(() => {});

      setDone(true);
      toast.success("Request received! We'll reach out shortly.");
      onSuccess?.();
    } catch (e) {
      toast.error("Could not submit. Please try again or call us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="quote-success" className="text-center py-10 px-4">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}>
          <CheckCircle2 className="w-16 h-16 text-honey mx-auto" />
        </motion.div>
        <h3 className="text-2xl font-display font-bold text-white mt-5">You're on the schedule list!</h3>
        <p className="text-zinc-400 mt-2 max-w-sm mx-auto">
          Thanks, {data.name.split(" ")[0]}. A HoneyBee specialist will contact you to confirm your free,
          no-obligation in-home evaluation.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="quote-form" className="w-full">
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${s <= step ? "bg-honey" : "bg-white/10"}`} />
        ))}
      </div>

      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-1">Step 1 of 3</p>
            <h3 className="text-xl font-display font-bold text-white mb-4">What do you need help with?</h3>
            <div className="grid grid-cols-1 gap-2">
              {SERVICE_OPTIONS.map((s) => (
                <button
                  key={s}
                  data-testid={`service-opt-${s.split(" ")[0].toLowerCase()}`}
                  onClick={() => set("service", s)}
                  className={`text-left px-4 py-3 rounded-sm border text-sm font-medium transition-colors ${
                    data.service === s ? "border-honey bg-honey/10 text-white" : "border-white/15 text-zinc-300 hover:border-white/30"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button onClick={next} data-testid="quote-next-1"
              className="w-full mt-5 bg-honey text-white font-bold py-3 rounded-sm flex items-center justify-center gap-2 hover:bg-honey-600 transition-colors">
              Continue <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {step === 2 && (
          <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-1">Step 2 of 3</p>
            <h3 className="text-xl font-display font-bold text-white mb-4">Tell us about the property</h3>
            <label className="text-xs text-zinc-400 font-semibold">How soon?</label>
            <div className="grid grid-cols-2 gap-2 mt-1 mb-4">
              {URGENCY.map((u) => (
                <button key={u} onClick={() => set("urgency", u)}
                  className={`px-3 py-2.5 rounded-sm border text-xs font-medium transition-colors ${
                    data.urgency === u ? "border-honey bg-honey/10 text-white" : "border-white/15 text-zinc-300 hover:border-white/30"
                  }`}>
                  {u}
                </button>
              ))}
            </div>
            <label className="text-xs text-zinc-400 font-semibold">Property type</label>
            <div className="grid grid-cols-3 gap-2 mt-1 mb-4">
              {PROPERTY.map((p) => (
                <button key={p} onClick={() => set("property_type", p)}
                  className={`px-2 py-2.5 rounded-sm border text-xs font-medium transition-colors ${
                    data.property_type === p ? "border-honey bg-honey/10 text-white" : "border-white/15 text-zinc-300 hover:border-white/30"
                  }`}>
                  {p}
                </button>
              ))}
            </div>
            <input data-testid="quote-city" className={inputCls} placeholder="City (e.g. Arlington, Everett)"
              value={data.city} onChange={(e) => set("city", e.target.value)} />
            <div className="flex gap-2 mt-5">
              <button onClick={back} className="px-4 py-3 rounded-sm border border-white/15 text-zinc-300 flex items-center gap-2 hover:border-white/30">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button onClick={next} data-testid="quote-next-2"
                className="flex-1 bg-honey text-white font-bold py-3 rounded-sm flex items-center justify-center gap-2 hover:bg-honey-600 transition-colors">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}

        {step === 3 && (
          <motion.div key="s3" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <p className="text-xs uppercase tracking-[0.2em] text-honey font-bold mb-1">Step 3 of 3</p>
            <h3 className="text-xl font-display font-bold text-white mb-4">Where should we send your quote?</h3>
            <div className="space-y-3">
              <input data-testid="quote-name" className={inputCls} placeholder="Full name *"
                value={data.name} onChange={(e) => set("name", e.target.value)} />
              <input data-testid="quote-phone" className={inputCls} placeholder="Phone number *"
                value={data.phone} onChange={(e) => set("phone", e.target.value)} />
              <input data-testid="quote-email" type="email" className={inputCls} placeholder="Email address *"
                value={data.email} onChange={(e) => set("email", e.target.value)} />
              <textarea data-testid="quote-message" rows={3} className={inputCls} placeholder="Anything else we should know? (optional)"
                value={data.message} onChange={(e) => set("message", e.target.value)} />
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={back} className="px-4 py-3 rounded-sm border border-white/15 text-zinc-300 flex items-center gap-2 hover:border-white/30">
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button onClick={submit} disabled={loading} data-testid="quote-submit"
                className="flex-1 bg-honey text-white font-bold py-3 rounded-sm flex items-center justify-center gap-2 hover:bg-honey-600 transition-colors disabled:opacity-60">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Get My Free Quote <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
            <div className="flex items-center justify-center gap-4 mt-4 text-xs text-zinc-500">
              <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5 text-honey" /> No spam</span>
              <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5 text-honey" /> 5.0 rated</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
